import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart, FREE_SHIP_THRESHOLD } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);

  const goToShopify = async () => {
    setLoading(true);
    try {
      const lines = items.map((i) => ({ variantId: i.variantId, quantity: i.quantity }));
      const { data } = await api.post("/checkout", { lines });
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl uppercase mb-4">Your Bag Is Empty</h1>
        <Link to="/shop/all" className="inline-block bg-ink text-cream px-8 py-4 text-xs font-bold uppercase tracking-widest">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 max-w-[720px] mx-auto px-4 md:px-8" data-testid="checkout-page">
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">Review Bag</h1>
      <p className="text-sm text-gray-500 mb-10">Payment & shipping are handled securely on Shopify.</p>

      <div className="bg-[#f9f9f7] p-6">
        <div className="flex flex-col gap-4 mb-6">
          {items.map((i) => (
            <div key={i.key} className="flex gap-3 items-center" data-testid={`checkout-line-${i.key}`}>
              <img src={i.image} alt={i.name} className="w-16 h-20 object-cover bg-gray-100" />
              <div className="flex-1">
                <p className="text-sm font-bold uppercase">{i.name}</p>
                <p className="text-xs text-gray-500">{[i.color, i.size].filter(Boolean).join(" · ")} · Qty {i.quantity}</p>
              </div>
              <span className="text-sm font-bold">{money(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500 uppercase tracking-wide">Subtotal</span><span className="font-bold" data-testid="checkout-subtotal">{money(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500 uppercase tracking-wide">Shipping</span><span className="font-bold">{remaining > 0 ? "Calculated at checkout" : "FREE"}</span></div>
        </div>
      </div>

      <button onClick={goToShopify} disabled={loading} className="mt-6 w-full bg-ink text-cream py-5 font-bold uppercase tracking-widest hover:bg-ink/85 transition-colors disabled:opacity-60 flex items-center justify-center gap-3" data-testid="place-order-btn">
        <Lock size={16} />
        {loading ? "Redirecting to Shopify..." : "Continue to Secure Checkout"}
      </button>
      <p className="text-[11px] text-gray-400 text-center uppercase tracking-widest mt-4 flex items-center justify-center gap-2">
        <ShieldCheck size={14} /> Powered by Shopify Checkout
      </p>
    </div>
  );
}
